import { cx } from '@emotion/css';
import { AnimatePresence, HTMLMotionProps, motion, Variants } from 'framer-motion';
import { Children, cloneElement, FC, isValidElement, ReactElement, ReactNode } from 'react';
import { FabButton } from './fab-button';

export type FabMenuItemProps = HTMLMotionProps<'button'> & { icon: string; children?: ReactNode };

export const FabMenuItem: FC<React.PropsWithChildren<FabMenuItemProps>> = ({ children, icon, ...otherProps }) => {
  return <FabButton {...otherProps} btnType="grey" size="small" icon={icon}></FabButton>;
};

export type FabMenuProps = HTMLMotionProps<'div'> & {
  children: ReactNode;
  open?: boolean;
  labelPosition?: 'left' | 'right';
  menuPosition?: 'top' | 'bottom';
};

export const FabMenu: FC<React.PropsWithChildren<FabMenuProps>> = ({
  children,
  open = false,
  labelPosition = 'left',
  menuPosition = 'top',
  ...otherProps
}) => {
  const container: Variants = {
    closed: {
      opacity: 0,
      transition: {
        staggerChildren: 0.15,
        staggerDirection: -1,
      },
    },
    open: {
      opacity: 1,
      scale: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const item: Variants = {
    closed: { opacity: 0, scale: 0 },
    open: { opacity: 1, scale: 1 },
  };

  return (
    <div
      className={cx(
        'absolute',
        'left-[calc((3.5rem-2.5rem)/2)]',
        menuPosition === 'top' ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]'
      )}
    >
      <AnimatePresence>
        <motion.div
          className={cx('flex gap-2', menuPosition === 'top' ? 'flex-col-reverse' : 'flex-col')}
          {...otherProps}
          variants={container}
          animate={open ? 'open' : 'closed'}
          initial="closed"
        >
          {Children.map(children, (child) => {
            if (isValidElement(child) && child.type === FabMenuItem) {
              const menuItemChildren = (child as ReactElement).props.children;
              return (
                <motion.div variants={item} className="relative flex flex-row items-center gap-2">
                  {menuItemChildren && (
                    <div
                      className={cx(
                        'absolute whitespace-nowrap bg-primary-white px-6 py-2 text-secondary-deep stihl-text-interactive-button',
                        labelPosition === 'left' ? 'right-6' : 'left-6',
                        labelPosition === 'left'
                          ? 'rounded-l-[2.8125rem] rounded-r-[0.0625rem]'
                          : 'rounded-l-[0.0625rem] rounded-r-[2.8125rem]'
                      )}
                    >
                      {menuItemChildren}
                    </div>
                  )}
                  {cloneElement(child)}
                </motion.div>
              );
            }
            throw new Error('Only <FabMenuItem /> components are inside FabMenu allowed');
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
